<script setup>
const { openImagePreview } = useImagePreview();
const activeTab = ref("photo-effect-data");

// 从 public 文件夹加载 photo_effect_data.json, video_effect_data.json, user_data.json, a.json 和 b.json
const photoEffectData = ref([]);
const videoEffectData = ref([]);
const userData = ref([]);
const a = ref([]);
const b = ref([]);

async function loadPhotoEffectData() {
  try {
    const res = await fetch("/pollo_ai/photo_effect_data.json");
    const data = await res.json();
    photoEffectData.value = Array.isArray(data) ? data.reverse() : [];
  } catch (e) {
    console.error("加载 photo_effect_data.json 失败:", e);
    photoEffectData.value = [];
  }
}

async function loadVideoEffectData() {
  try {
    const res = await fetch("/pollo_ai/video_effect_data.json");
    const data = await res.json();
    videoEffectData.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 video_effect_data.json 失败:", e);
    videoEffectData.value = [];
  }
}

async function loadUserData() {
  try {
    const res = await fetch("/pollo_ai/user_data.json");
    const data = await res.json();
    userData.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 user_data.json 失败:", e);
    userData.value = [];
  }
}

async function loadA() {
  try {
    const res = await fetch("/pollo_ai/a.json");
    const data = await res.json();
    a.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 a.json 失败:", e);
    a.value = [];
  }
}

async function loadB() {
  try {
    const res = await fetch("/pollo_ai/b.json");
    const data = await res.json();
    b.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 b.json 失败:", e);
    b.value = [];
  }
}

// 自动合并项目 public/pollo_ai/photo_data 文件夹下全部 json 文件并按 videoId 去重
const polloAiPhotoDataDir = "/pollo_ai/photo_data/"; // public 下的 pollo_ai/photo_data 文件夹

// 从 index.json 读取所有 json 文件名列表
let polloAiJsonFiles = [];

async function loadPhotoDataJsonFileList() {
  try {
    const res = await fetch(`${polloAiPhotoDataDir}index.json`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 photo_data index.json 失败:", e);
    return [];
  }
}

async function loadAllPhotoDataJsonAndMergeUniqueByVideoId() {
  const polloAiPhotoDataJsonFiles = await loadPhotoDataJsonFileList();
  let allData = [];
  for (let file of polloAiPhotoDataJsonFiles) {
    try {
      const res = await fetch(`${polloAiPhotoDataDir}${file}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        allData = allData.concat(data);
      } else {
        allData.push(data);
      }
    } catch (e) {
      console.error(`加载 ${file} 失败:`, e);
    }
  }
  // 按 videoId 去重
  const uniqueMap = {};
  allData.forEach((item) => {
    if (item && item.videoId) {
      uniqueMap[item.videoId] = item;
    }
  });
  return Object.values(uniqueMap);
}

// Photo Effects Inspirations (原数据 C) 需要由 async 数据加载得到
const photoEffectsInspirations = ref([]);

// 自动合并项目 public/pollo_ai/photo_group 文件夹下全部 json 文件并按 templateCategory 分组
const polloAiPhotoGroupDir = "/pollo_ai/photo_group/"; // public 下的 pollo_ai/photo_group 文件夹

async function loadPhotoGroupJsonFileList() {
  try {
    const res = await fetch(`${polloAiPhotoGroupDir}index.json`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 photo_group index.json 失败:", e);
    return [];
  }
}

async function loadAllPhotoGroupJsonAndMerge() {
  const polloAiPhotoGroupJsonFiles = await loadPhotoGroupJsonFileList();
  let allData = [];
  for (let file of polloAiPhotoGroupJsonFiles) {
    try {
      const res = await fetch(`${polloAiPhotoGroupDir}${file}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        allData = allData.concat(data);
      } else {
        allData.push(data);
      }
    } catch (e) {
      console.error(`加载 ${file} 失败:`, e);
    }
  }
  return allData;
}

// Photo Effects (原数据 D) 需要由 async 数据加载得到
const photoEffects = ref([]);

// Video Effects Inspirations 需要由 async 数据加载得到
const videoEffectsInspirations = ref([]);

// 自动合并项目 public/pollo_ai/video_data 文件夹下全部 json 文件并按 videoId 去重
const polloAiVideoDataDir = "/pollo_ai/video_data/"; // public 下的 pollo_ai/video_data 文件夹

async function loadVideoDataJsonFileList() {
  try {
    const res = await fetch(`${polloAiVideoDataDir}index.json`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 video_data index.json 失败:", e);
    return [];
  }
}

async function loadAllVideoDataJsonAndMergeUniqueByVideoId() {
  const polloAiVideoDataJsonFiles = await loadVideoDataJsonFileList();
  let allData = [];
  for (let file of polloAiVideoDataJsonFiles) {
    try {
      const res = await fetch(`${polloAiVideoDataDir}${file}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        allData = allData.concat(data);
      } else {
        allData.push(data);
      }
    } catch (e) {
      console.error(`加载 ${file} 失败:`, e);
    }
  }
  // 按 videoId 去重
  const uniqueMap = {};
  allData.forEach((item) => {
    if (item && item.videoId) {
      uniqueMap[item.videoId] = item;
    }
  });
  return Object.values(uniqueMap);
}

// 自动合并项目 public/pollo_ai/video_group 文件夹下全部 json 文件并按 templateCategory 分组
const polloAiVideoGroupDir = "/pollo_ai/video_group/"; // public 下的 pollo_ai/video_group 文件夹

async function loadVideoGroupJsonFileList() {
  try {
    const res = await fetch(`${polloAiVideoGroupDir}index.json`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error("加载 video_group index.json 失败:", e);
    return [];
  }
}

async function loadAllVideoGroupJsonAndMerge() {
  const polloAiVideoGroupJsonFiles = await loadVideoGroupJsonFileList();
  let allData = [];
  for (let file of polloAiVideoGroupJsonFiles) {
    try {
      const res = await fetch(`${polloAiVideoGroupDir}${file}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        allData = allData.concat(data);
      } else {
        allData.push(data);
      }
    } catch (e) {
      console.error(`加载 ${file} 失败:`, e);
    }
  }
  return allData;
}

// Video Effects 需要由 async 数据加载得到
const videoEffects = ref([]);

// 初始化加载所有数据
Promise.all([
  loadPhotoEffectData(),
  loadVideoEffectData(),
  loadUserData(),
  loadA(),
  loadB(),
  loadAllPhotoDataJsonAndMergeUniqueByVideoId(),
  loadAllPhotoGroupJsonAndMerge(),
  loadAllVideoDataJsonAndMergeUniqueByVideoId(),
  loadAllVideoGroupJsonAndMerge(),
]).then(
  ([
    ,
    ,
    ,
    ,
    ,
    resPhotoInspirations,
    resPhotoEffects,
    resVideoInspirations,
    resVideoEffects,
  ]) => {
    photoEffectsInspirations.value = resPhotoInspirations;
    photoEffects.value = resPhotoEffects;
    videoEffectsInspirations.value = resVideoInspirations;
    videoEffects.value = resVideoEffects;
  }
);

const tabItems = computed(() => [
  {
    label: `Photo Effect Data(${photoEffectData.value.length})`,
    value: "photo-effect-data",
    key: "photo-effect-data",
  },
  {
    label: `Video Effect Data(${videoEffectData.value.length})`,
    value: "video-effect-data",
    key: "video-effect-data",
  },
  {
    label: `Photo Effects(${photoEffects.value?.length || 0})`,
    value: "photo-effects",
    key: "photo-effects",
  },
  {
    label: `Photo Effects Inspirations(${
      photoEffectsInspirations.value?.length || 0
    })`,
    value: "photo-inspirations",
    key: "photo-inspirations",
  },
  {
    label: `Video Effects(${videoEffects.value?.length || 0})`,
    value: "video-effects",
    key: "video-effects",
  },
  {
    label: `Video Effects Inspirations(${
      videoEffectsInspirations.value?.length || 0
    })`,
    value: "video-inspirations",
    key: "video-inspirations",
  },
  {
    label: `用户数据(${userData.value.length})`,
    value: "user-data",
    key: "user-data",
  },
  { label: `数据 A(${a.value.length})`, value: "a", key: "a" },
  // { label: `数据 B(${b.value.length})`, value: "b", key: "b" },
]);

// 按 title 分组 Photo Effects Inspirations，并收集去重后的提示词
const groupedPhotoInspirations = computed(() => {
  const groups = {};
  photoEffectsInspirations.value.forEach((item) => {
    const title = item?.generateRecord?.generationTemplate?.title || "未分类";
    if (!groups[title]) {
      groups[title] = {
        items: [],
        prompts: new Set(), // 使用 Set 自动去重
      };
    }
    groups[title].items.push(item);
    // 收集提示词（排除 "protect"）
    const prompt = item?.generateRecord?.processConfig?.prompt;
    if (prompt && typeof prompt === "string" && prompt.trim()) {
      const trimmedPrompt = prompt.trim();
      // 如果提示词不是 "protect"，才添加到汇总中
      if (trimmedPrompt.toLowerCase() !== "protect") {
        groups[title].prompts.add(trimmedPrompt);
      }
    }
  });
  // 转换为数组格式，便于模板遍历
  return Object.keys(groups).map((title) => ({
    title,
    items: groups[title].items,
    prompts: Array.from(groups[title].prompts), // 转换为数组
  }));
});

// 按 title 分组 Video Effects Inspirations
const groupedVideoInspirations = computed(() => {
  const groups = {};
  videoEffectsInspirations.value.forEach((item) => {
    const title = item?.generateRecord?.generationTemplate?.title || "未分类";
    if (!groups[title]) {
      groups[title] = {
        items: [],
        hasWorkflowConfig: false,
      };
    }
    groups[title].items.push(item);
    // 标记该组是否有 workflowConfig
    if (item?.generateRecord?.generationConfig?.workflowConfig) {
      groups[title].hasWorkflowConfig = true;
    }
  });
  // 转换为数组格式，便于模板遍历
  const groupArray = Object.keys(groups).map((title) => ({
    title,
    items: groups[title].items,
    hasWorkflowConfig: groups[title].hasWorkflowConfig,
  }));
  // 统计总标签数和有配置的标签数
  const totalGroups = groupArray.length;
  const groupsWithConfig = groupArray.filter((g) => g.hasWorkflowConfig).length;
  return {
    groups: groupArray,
    totalGroups,
    groupsWithConfig,
  };
});

// 控制每个分组提示词的展开/折叠状态
const promptExpanded = ref({});

function togglePrompt(groupTitle) {
  promptExpanded.value[groupTitle] = !promptExpanded.value[groupTitle];
}

// Photo Effects Inspirations - 当前选中的组标题
const selectedPhotoInspirationsGroupTitle = ref(null);

// Photo Effects Inspirations - 当前选中的组数据
const currentPhotoInspirationsGroup = computed(() => {
  if (!selectedPhotoInspirationsGroupTitle.value) return null;
  return groupedPhotoInspirations.value.find(
    (group) => group.title === selectedPhotoInspirationsGroupTitle.value
  );
});

// 当分组数据加载完成后，默认选中第一个组
watch(
  groupedPhotoInspirations,
  (newGroups) => {
    if (newGroups.length > 0 && !selectedPhotoInspirationsGroupTitle.value) {
      selectedPhotoInspirationsGroupTitle.value = newGroups[0].title;
    }
  },
  { immediate: true }
);

// Video Effects Inspirations - 当前选中的组标题
const selectedVideoInspirationsGroupTitle = ref(null);

// Video Effects Inspirations - 当前选中的组数据
const currentVideoInspirationsGroup = computed(() => {
  if (!selectedVideoInspirationsGroupTitle.value) return null;
  return groupedVideoInspirations.value.groups.find(
    (group) => group.title === selectedVideoInspirationsGroupTitle.value
  );
});

// 当分组数据加载完成后，默认选中第一个组
watch(
  groupedVideoInspirations,
  (newGroups) => {
    if (
      newGroups.groups &&
      newGroups.groups.length > 0 &&
      !selectedVideoInspirationsGroupTitle.value
    ) {
      selectedVideoInspirationsGroupTitle.value = newGroups.groups[0].title;
    }
  },
  { immediate: true }
);

// 按 templateCategory 分组 Photo Effects
const groupedPhotoEffects = computed(() => {
  const groups = {};
  photoEffects.value.forEach((item) => {
    const category = item?.templateCategory || "未分类";
    if (!groups[category]) {
      groups[category] = {
        items: [],
      };
    }
    groups[category].items.push(item);
  });
  return Object.keys(groups).map((category) => ({
    category,
    items: groups[category].items,
  }));
});

// Photo Effects - 当前选中的组类别
const selectedPhotoEffectsCategory = ref(null);

// Photo Effects - 当前选中的组数据
const currentPhotoEffectsGroup = computed(() => {
  if (!selectedPhotoEffectsCategory.value) return null;
  return groupedPhotoEffects.value.find(
    (group) => group.category === selectedPhotoEffectsCategory.value
  );
});

// 当分组数据加载完成后，默认选中第一个组
watch(
  groupedPhotoEffects,
  (newGroups) => {
    if (newGroups.length > 0 && !selectedPhotoEffectsCategory.value) {
      selectedPhotoEffectsCategory.value = newGroups[0].category;
    }
  },
  { immediate: true }
);

// 按 templateCategory 分组 Video Effects
const groupedVideoEffects = computed(() => {
  const groups = {};
  videoEffects.value.forEach((item) => {
    const category = item?.templateCategory || "未分类";
    if (!groups[category]) {
      groups[category] = {
        items: [],
      };
    }
    groups[category].items.push(item);
  });
  return Object.keys(groups).map((category) => ({
    category,
    items: groups[category].items,
  }));
});

// Video Effects - 当前选中的组类别
const selectedVideoEffectsCategory = ref(null);

// Video Effects - 当前选中的组数据
const currentVideoEffectsGroup = computed(() => {
  if (!selectedVideoEffectsCategory.value) return null;
  return groupedVideoEffects.value.find(
    (group) => group.category === selectedVideoEffectsCategory.value
  );
});

// 当分组数据加载完成后，默认选中第一个组
watch(
  groupedVideoEffects,
  (newGroups) => {
    if (newGroups.length > 0 && !selectedVideoEffectsCategory.value) {
      selectedVideoEffectsCategory.value = newGroups[0].category;
    }
  },
  { immediate: true }
);

// 检查 Photo Effects 的 title 是否在 Photo Effects Inspirations 的组标题中存在
function hasMatchingGroupInPhotoInspirations(itemTitle) {
  if (!itemTitle) return false;
  const title = itemTitle.trim().toLowerCase();
  return groupedPhotoInspirations.value.some((group) => {
    const groupTitle = (group.title || "").toLowerCase();
    return groupTitle.includes(title);
  });
}

// 获取 Photo Effects 的 title 在 Photo Effects Inspirations 中匹配的组标题
function getMatchingGroupTitleInPhotoInspirations(itemTitle) {
  if (!itemTitle) return null;
  const title = itemTitle.trim().toLowerCase();
  const matchedGroup = groupedPhotoInspirations.value.find((group) => {
    const groupTitle = (group.title || "").toLowerCase();
    return groupTitle.includes(title);
  });
  return matchedGroup ? matchedGroup.title : null;
}

// 点击 Photo Effects 卡片，跳转到 Photo Effects Inspirations 并选中对应的组
function navigateToPhotoInspirationsFromPhotoEffects(itemTitle) {
  const matchingTitle = getMatchingGroupTitleInPhotoInspirations(itemTitle);
  if (matchingTitle) {
    activeTab.value = "photo-inspirations";
    selectedPhotoInspirationsGroupTitle.value = matchingTitle;
    nextTick(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }
}

// 检查 Video Effects 的 title 是否在 Video Effects Inspirations 的组标题中存在
function hasMatchingGroupInVideoInspirations(itemTitle) {
  if (!itemTitle) return false;
  const title = itemTitle.trim().toLowerCase();
  return groupedVideoInspirations.value.groups.some((group) => {
    const groupTitle = (group.title || "").toLowerCase();
    return groupTitle.includes(title);
  });
}

// 获取 Video Effects 的 title 在 Video Effects Inspirations 中匹配的组标题
function getMatchingGroupTitleInVideoInspirations(itemTitle) {
  if (!itemTitle) return null;
  const title = itemTitle.trim().toLowerCase();
  const matchedGroup = groupedVideoInspirations.value.groups.find((group) => {
    const groupTitle = (group.title || "").toLowerCase();
    return groupTitle.includes(title);
  });
  return matchedGroup ? matchedGroup.title : null;
}

// 点击 Video Effects 卡片，跳转到 Video Effects Inspirations 并选中对应的组
function navigateToVideoInspirationsFromVideoEffects(itemTitle) {
  const matchingTitle = getMatchingGroupTitleInVideoInspirations(itemTitle);
  if (matchingTitle) {
    activeTab.value = "video-inspirations";
    selectedVideoInspirationsGroupTitle.value = matchingTitle;
    nextTick(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }
}
</script>

<template>
  <section class="max-w-full mx-auto p-4 md:p-6 bg-gray-50">
    <h1 class="text-3xl font-bold text-gray-800 mb-8 text-center">数据展示</h1>

    <UTabs
      v-model="activeTab"
      :items="tabItems"
      class="mb-8"
      color="primary"
      value-key="key"
      :ui="{
        list: 'bg-white',
        trigger: 'text-gray-800 data-[state=inactive]:text-gray-600',
      }"
    />

    <!-- Photo Effect Data 展示 -->
    <div v-if="activeTab === 'photo-effect-data'">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="item in photoEffectData"
          :key="item.templateCode"
          class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- 封面图片 -->
          <div class="relative">
            <img
              loading="lazy"
              v-if="item.coverImg"
              :src="item.coverImg"
              :alt="item.title"
              class="w-full h-48 object-cover cursor-pointer"
              @click="openImagePreview(item.coverImg)"
            />
            <div
              v-else
              class="w-full h-48 bg-gray-200 flex items-center justify-center"
            >
              <span class="text-gray-400">无封面</span>
            </div>
            <div
              v-if="item.templateCategory"
              class="absolute top-2 left-2 bg-indigo-600/80 text-white px-2 py-1 rounded text-xs font-medium"
            >
              {{ item.categoryName || item.templateCategory }}
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="p-4">
            <!-- 标题 -->
            <div class="mb-3">
              <h3 class="text-lg font-semibold text-gray-800 mb-1">
                {{ item.title || "无标题" }}
              </h3>
              <p
                v-if="item.originalTitle && item.originalTitle !== item.title"
                class="text-xs text-gray-500"
              >
                原名: {{ item.originalTitle }}
              </p>
            </div>

            <!-- 基本信息 -->
            <div class="text-xs text-gray-500 mb-3 space-y-1">
              <p v-if="item.templateCode">
                <span class="font-medium">模板代码:</span>
                <span class="font-mono text-xs">{{ item.templateCode }}</span>
              </p>
              <p v-if="item.templateCategory">
                <span class="font-medium">分类:</span>
                {{ item.templateCategory }}
              </p>
              <p v-if="item.categoryName">
                <span class="font-medium">分类名称:</span>
                {{ item.categoryName }}
              </p>
            </div>

            <!-- Prompt -->
            <div v-if="item.prompt" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">Prompt:</p>
              <p
                class="text-xs leading-relaxed text-gray-600 wrap-break-word max-h-[200px] overflow-y-auto p-2 bg-gray-50 rounded border-l-4 border-indigo-500"
              >
                {{ item.prompt }}
              </p>
            </div>

            <!-- Cover Webp -->
            <div v-if="item.coverWebp" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webp:
              </p>
              <img
                loading="lazy"
                :src="item.coverWebp"
                alt="Cover Webp"
                class="w-full h-32 object-cover rounded cursor-pointer border border-gray-200"
                @click="openImagePreview(item.coverWebp)"
              />
            </div>

            <!-- Cover Webps 图片数组 -->
            <div
              v-if="item.coverWebps && item.coverWebps.length > 0"
              class="mb-3"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webps ({{ item.coverWebps.length }}):
              </p>
              <div class="flex gap-2 overflow-x-auto">
                <img
                  loading="lazy"
                  v-for="(webp, idx) in item.coverWebps"
                  :key="idx"
                  :src="webp"
                  :alt="`${item.title} cover ${idx + 1}`"
                  class="w-24 h-24 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                  @click="openImagePreview(webp)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Effect Data 展示 -->
    <div v-if="activeTab === 'video-effect-data'">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="item in videoEffectData"
          :key="item.templateCode"
          class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- 封面图片 -->
          <div class="relative">
            <img
              loading="lazy"
              v-if="item.coverImg"
              :src="item.coverImg"
              :alt="item.title"
              class="w-full h-48 object-cover cursor-pointer"
              @click="openImagePreview(item.coverImg)"
            />
            <div
              v-else
              class="w-full h-48 bg-gray-200 flex items-center justify-center"
            >
              <span class="text-gray-400">无封面</span>
            </div>
            <div
              v-if="item.templateCategory"
              class="absolute top-2 left-2 bg-indigo-600/80 text-white px-2 py-1 rounded text-xs font-medium"
            >
              {{ item.categoryName || item.templateCategory }}
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="p-4">
            <!-- 标题 -->
            <div class="mb-3">
              <h3 class="text-lg font-semibold text-gray-800 mb-1">
                {{ item.title || "无标题" }}
              </h3>
              <p
                v-if="item.originalTitle && item.originalTitle !== item.title"
                class="text-xs text-gray-500"
              >
                原名: {{ item.originalTitle }}
              </p>
            </div>

            <!-- 基本信息 -->
            <div class="text-xs text-gray-500 mb-3 space-y-1">
              <p v-if="item.templateCode">
                <span class="font-medium">模板代码:</span>
                <span class="font-mono text-xs">{{ item.templateCode }}</span>
              </p>
              <p v-if="item.templateCategory">
                <span class="font-medium">分类:</span>
                {{ item.templateCategory }}
              </p>
              <p v-if="item.categoryName">
                <span class="font-medium">分类名称:</span>
                {{ item.categoryName }}
              </p>
            </div>

            <!-- Cover Webp -->
            <div v-if="item.coverWebp" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webp:
              </p>
              <div
                class="w-full h-32 bg-gray-50 rounded border border-gray-200 flex items-center justify-center overflow-hidden"
                style="position: relative"
              >
                <img
                  loading="lazy"
                  :src="item.coverWebp"
                  alt="Cover Webp"
                  class="max-w-full max-h-full object-contain rounded cursor-pointer"
                  style="display: block"
                  @click="openImagePreview(item.coverWebp)"
                />
              </div>
            </div>

            <!-- Cover Webps 图片数组 -->
            <div
              v-if="item.coverWebps && item.coverWebps.length > 0"
              class="mb-3"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webps ({{ item.coverWebps.length }}):
              </p>
              <div class="flex gap-2 overflow-x-auto">
                <img
                  loading="lazy"
                  v-for="(webp, idx) in item.coverWebps"
                  :key="idx"
                  :src="webp"
                  :alt="`${item.title} cover ${idx + 1}`"
                  class="w-24 h-24 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                  @click="openImagePreview(webp)"
                />
              </div>
            </div>

            <!-- Workflow Config -->
            <div
              v-if="item.workflowConfig"
              class="mb-3 pt-3 border-t border-gray-200"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Workflow Config:
              </p>

              <!-- Workflow Config dag -->
              <div class="mb-3">
                <p class="text-xs font-semibold text-gray-600 mb-2">dag:</p>
                <div
                  class="w-full bg-gray-50 rounded border border-gray-200 flex items-center justify-center overflow-x-auto"
                  style="max-width: 100%"
                >
                  <img
                    loading="lazy"
                    :src="`/pollo_ai/video_workflow_dag/${item.templateCode}.png`"
                    alt="Workflow Config dag"
                    class="max-w-full max-h-48 object-contain rounded cursor-pointer"
                    style="display: block"
                    @click="
                      openImagePreview(
                        `/pollo_ai/video_workflow_dag/${item.templateCode}.png`
                      )
                    "
                  />
                </div>
              </div>

              <!-- genImage -->
              <div
                v-if="
                  item.workflowConfig.genImage &&
                  item.workflowConfig.genImage.enable
                "
                class="mb-3 p-2 bg-blue-50 rounded border-l-4 border-blue-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Image:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.workflowConfig.genImage.enable !== undefined">
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genImage.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genImage.model">
                    <span class="font-medium">模型:</span>
                    {{ item.workflowConfig.genImage.model }}
                  </p>
                  <p v-if="item.workflowConfig.genImage.aspectRatio">
                    <span class="font-medium">宽高比:</span>
                    {{ item.workflowConfig.genImage.aspectRatio }}
                  </p>
                  <p v-if="item.workflowConfig.genImage.prompt" class="mt-1">
                    <span class="font-medium">Prompt:</span>
                    <span
                      class="block mt-1 p-1 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[150px] overflow-y-auto text-xs"
                    >
                      {{ item.workflowConfig.genImage.prompt }}
                    </span>
                  </p>
                  <div v-if="item.workflowConfig.genImage.image" class="mt-1">
                    <p class="font-medium mb-1 text-xs">图片:</p>
                    <img
                      loading="lazy"
                      :src="item.workflowConfig.genImage.image"
                      alt="Gen Image"
                      class="w-24 h-24 object-cover rounded cursor-pointer border border-gray-200"
                      @click="
                        openImagePreview(item.workflowConfig.genImage.image)
                      "
                    />
                  </div>
                  <div
                    v-if="
                      item.workflowConfig.genImage.refImages &&
                      item.workflowConfig.genImage.refImages.length > 0
                    "
                    class="mt-1"
                  >
                    <p class="font-medium mb-1 text-xs">参考图片:</p>
                    <div class="flex gap-1 overflow-x-auto">
                      <img
                        loading="lazy"
                        v-for="(img, idx) in item.workflowConfig.genImage
                          .refImages"
                        :key="idx"
                        :src="img"
                        :alt="`参考图片 ${idx + 1}`"
                        class="w-16 h-16 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                        @click="openImagePreview(img)"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- genVideo -->
              <div
                v-if="
                  item.workflowConfig.genVideo &&
                  item.workflowConfig.genVideo.enable
                "
                class="mb-3 p-2 bg-green-50 rounded border-l-4 border-green-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Video:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.workflowConfig.genVideo.enable !== undefined">
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genVideo.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.model">
                    <span class="font-medium">模型:</span>
                    {{ item.workflowConfig.genVideo.model }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.resolution">
                    <span class="font-medium">分辨率:</span>
                    {{ item.workflowConfig.genVideo.resolution }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.aspectRatio">
                    <span class="font-medium">宽高比:</span>
                    {{ item.workflowConfig.genVideo.aspectRatio }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.length">
                    <span class="font-medium">长度:</span>
                    {{ item.workflowConfig.genVideo.length }}秒
                  </p>
                  <p v-if="item.workflowConfig.genVideo.prompt" class="mt-1">
                    <span class="font-medium">Prompt:</span>
                    <span
                      class="block mt-1 p-1 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[150px] overflow-y-auto text-xs"
                    >
                      {{ item.workflowConfig.genVideo.prompt }}
                    </span>
                  </p>
                  <p
                    v-if="
                      item.workflowConfig.genVideo.promptOptimizer !== undefined
                    "
                  >
                    <span class="font-medium">Prompt Optimizer:</span>
                    {{
                      item.workflowConfig.genVideo.promptOptimizer ? "是" : "否"
                    }}
                  </p>
                  <p
                    v-if="
                      item.workflowConfig.genVideo.generateAudio !== undefined
                    "
                  >
                    <span class="font-medium">生成音频:</span>
                    {{
                      item.workflowConfig.genVideo.generateAudio ? "是" : "否"
                    }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.wanAudio !== undefined">
                    <span class="font-medium">Wan Audio:</span>
                    {{ item.workflowConfig.genVideo.wanAudio ? "是" : "否" }}
                  </p>
                  <p
                    v-if="
                      item.workflowConfig.genVideo.enableImageTail !== undefined
                    "
                  >
                    <span class="font-medium">启用图片尾部:</span>
                    {{
                      item.workflowConfig.genVideo.enableImageTail ? "是" : "否"
                    }}
                  </p>
                  <p v-if="item.workflowConfig.genVideo.imageTail">
                    <span class="font-medium">图片尾部:</span>
                    {{ item.workflowConfig.genVideo.imageTail }}
                  </p>
                </div>
              </div>

              <!-- genAudio -->
              <div
                v-if="
                  item.workflowConfig.genAudio &&
                  item.workflowConfig.genAudio.enable
                "
                class="mb-3 p-2 bg-purple-50 rounded border-l-4 border-purple-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Audio:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.workflowConfig.genAudio.enable !== undefined">
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genAudio.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genAudio.model">
                    <span class="font-medium">模型:</span>
                    {{ item.workflowConfig.genAudio.model }}
                  </p>
                  <p v-if="item.workflowConfig.genAudio.prompt" class="mt-1">
                    <span class="font-medium">Prompt:</span>
                    <span
                      class="block mt-1 p-1 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[150px] overflow-y-auto text-xs"
                    >
                      {{ item.workflowConfig.genAudio.prompt }}
                    </span>
                  </p>
                  <p v-if="item.workflowConfig.genAudio.length">
                    <span class="font-medium">长度:</span>
                    {{ item.workflowConfig.genAudio.length }}秒
                  </p>
                </div>
              </div>

              <!-- genBgm -->
              <div
                v-if="
                  item.workflowConfig.genBgm &&
                  item.workflowConfig.genBgm.enable
                "
                class="mb-3 p-2 bg-yellow-50 rounded border-l-4 border-yellow-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">Gen BGM:</p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.workflowConfig.genBgm.enable !== undefined">
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genBgm.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genBgm.bgmUrl">
                    <span class="font-medium">BGM URL:</span>
                    <a
                      :href="item.workflowConfig.genBgm.bgmUrl"
                      target="_blank"
                      class="text-blue-600 hover:underline break-all text-xs"
                    >
                      {{ item.workflowConfig.genBgm.bgmUrl }}
                    </a>
                  </p>
                </div>
              </div>

              <!-- genSplice -->
              <div
                v-if="
                  item.workflowConfig.genSplice &&
                  item.workflowConfig.genSplice.enable
                "
                class="mb-3 p-2 bg-red-50 rounded border-l-4 border-red-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Splice:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.workflowConfig.genSplice.enable !== undefined">
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genSplice.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genSplice.location">
                    <span class="font-medium">位置:</span>
                    {{ item.workflowConfig.genSplice.location }}
                  </p>
                </div>
              </div>

              <!-- genTailImage -->
              <div
                v-if="
                  item.workflowConfig.genTailImage &&
                  item.workflowConfig.genTailImage.enable
                "
                class="mb-3 p-2 bg-indigo-50 rounded border-l-4 border-indigo-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Tail Image:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p
                    v-if="item.workflowConfig.genTailImage.enable !== undefined"
                  >
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genTailImage.enable ? "是" : "否" }}
                  </p>
                  <p v-if="item.workflowConfig.genTailImage.model">
                    <span class="font-medium">模型:</span>
                    {{ item.workflowConfig.genTailImage.model }}
                  </p>
                  <p v-if="item.workflowConfig.genTailImage.aspectRatio">
                    <span class="font-medium">宽高比:</span>
                    {{ item.workflowConfig.genTailImage.aspectRatio }}
                  </p>
                  <p
                    v-if="item.workflowConfig.genTailImage.prompt"
                    class="mt-1"
                  >
                    <span class="font-medium">Prompt:</span>
                    <span
                      class="block mt-1 p-1 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[150px] overflow-y-auto text-xs"
                    >
                      {{ item.workflowConfig.genTailImage.prompt }}
                    </span>
                  </p>
                </div>
              </div>

              <!-- genVideoEdit -->
              <div
                v-if="
                  item.workflowConfig.genVideoEdit &&
                  item.workflowConfig.genVideoEdit.enable
                "
                class="mb-3 p-2 bg-pink-50 rounded border-l-4 border-pink-500"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">
                  Gen Video Edit:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p
                    v-if="item.workflowConfig.genVideoEdit.enable !== undefined"
                  >
                    <span class="font-medium">启用:</span>
                    {{ item.workflowConfig.genVideoEdit.enable ? "是" : "否" }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户数据展示 -->
    <div v-if="activeTab === 'user-data'">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="item in userData"
          :key="item.id || item.videoId"
          class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- 封面图片 -->
          <div class="relative">
            <img
              loading="lazy"
              v-if="item.cover"
              :src="item.cover"
              :alt="item.title || '封面'"
              class="w-full h-48 object-cover cursor-pointer"
              @click="openImagePreview(item.cover)"
            />
            <div
              v-else
              class="w-full h-48 bg-gray-200 flex items-center justify-center"
            >
              <span class="text-gray-400">无封面</span>
            </div>
            <div
              v-if="item.starNum"
              class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
            >
              <span>⭐</span>
              <span>{{ item.starNum }}</span>
            </div>
            <div
              v-if="item.mediaType"
              class="absolute top-2 left-2 bg-blue-600/80 text-white px-2 py-1 rounded text-xs font-medium"
            >
              {{ item.mediaType }}
            </div>
            <div
              v-if="item.status"
              :class="[
                'absolute bottom-2 right-2 px-2 py-1 rounded text-xs font-medium',
                item.status === 'succeed'
                  ? 'bg-green-500/80 text-white'
                  : 'bg-red-500/80 text-white',
              ]"
            >
              {{ item.status }}
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="p-4">
            <!-- 标题 -->
            <div class="mb-3">
              <h3 class="text-lg font-semibold text-gray-800 mb-2">
                {{
                  item.title ||
                  item.generateRecord?.generationTemplate?.title ||
                  "无标题"
                }}
              </h3>
            </div>

            <!-- 基本信息 -->
            <div class="text-xs text-gray-500 mb-3 space-y-1">
              <p v-if="item.id">
                <span class="font-medium">ID:</span> {{ item.id }}
              </p>
              <p v-if="item.videoId">
                <span class="font-medium">Video ID:</span>
                <span class="font-mono text-xs">{{ item.videoId }}</span>
              </p>
              <p v-if="item.videoRatio">
                <span class="font-medium">宽高比:</span> {{ item.videoRatio }}
              </p>
              <p v-if="item.score !== undefined">
                <span class="font-medium">评分:</span> {{ item.score }}
              </p>
              <p v-if="item.publishStatus">
                <span class="font-medium">发布状态:</span>
                <span
                  :class="[
                    'ml-1 px-2 py-0.5 rounded text-xs',
                    item.publishStatus === 'published'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700',
                  ]"
                >
                  {{ item.publishStatus }}
                </span>
              </p>
              <p v-if="item.visibleStatus">
                <span class="font-medium">可见状态:</span>
                {{ item.visibleStatus }}
              </p>
            </div>

            <!-- 统计数据 -->
            <div
              v-if="
                item.starNum ||
                item.shareNum ||
                item.playNum ||
                item.downloadNum
              "
              class="mb-3 p-2 bg-gray-50 rounded"
            >
              <p class="text-xs font-semibold text-gray-600 mb-1">统计数据:</p>
              <div class="flex flex-wrap gap-2 text-xs">
                <span v-if="item.starNum" class="text-yellow-600">
                  ⭐ {{ item.starNum }}
                </span>
                <span v-if="item.shareNum" class="text-blue-600">
                  🔗 {{ item.shareNum }}
                </span>
                <span v-if="item.playNum" class="text-green-600">
                  ▶️ {{ item.playNum }}
                </span>
                <span v-if="item.downloadNum" class="text-purple-600">
                  ⬇️ {{ item.downloadNum }}
                </span>
              </div>
            </div>

            <!-- 标签 -->
            <!-- <div v-if="item.tags && item.tags.length > 0" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">标签:</p>
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="(tag, idx) in item.tags"
                  :key="idx"
                  class="inline-block px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
                >
                  {{ tag }}
                </span>
              </div>
            </div> -->

            <!-- 视频元数据 -->
            <div v-if="item.videoMeta" class="mb-3 p-2 bg-gray-50 rounded">
              <p class="text-xs font-semibold text-gray-600 mb-1">
                视频元数据:
              </p>
              <div class="text-xs text-gray-500 space-y-0.5">
                <p v-if="item.videoMeta.width && item.videoMeta.height">
                  <span class="font-medium">尺寸:</span>
                  {{ item.videoMeta.width }} × {{ item.videoMeta.height }}
                </p>
                <p v-if="item.videoMeta.aspectRatio">
                  <span class="font-medium">宽高比:</span>
                  {{ item.videoMeta.aspectRatio }}
                </p>
                <p v-if="item.videoMeta.duration !== undefined">
                  <span class="font-medium">时长:</span>
                  {{ item.videoMeta.duration }}s
                </p>
                <p v-if="item.videoMeta.hasAudio !== undefined">
                  <span class="font-medium">音频:</span>
                  {{ item.videoMeta.hasAudio ? "有" : "无" }}
                </p>
              </div>
            </div>

            <!-- 链接 -->
            <div
              v-if="item.videoUrl || item.videoUrlNoWatermark || item.mediaUrl"
              class="mb-3"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">链接:</p>
              <div class="flex flex-col gap-1">
                <a
                  v-if="item.videoUrl"
                  :href="item.videoUrl"
                  target="_blank"
                  class="text-xs text-blue-600 hover:underline truncate"
                >
                  视频链接
                </a>
                <a
                  v-if="item.videoUrlNoWatermark"
                  :href="item.videoUrlNoWatermark"
                  target="_blank"
                  class="text-xs text-blue-600 hover:underline truncate"
                >
                  无水印链接
                </a>
                <a
                  v-if="item.mediaUrl"
                  :href="item.mediaUrl"
                  target="_blank"
                  class="text-xs text-blue-600 hover:underline truncate"
                >
                  媒体链接
                </a>
              </div>
            </div>

            <!-- 缩略图 -->
            <div v-if="item.thumbnail" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">缩略图:</p>
              <img
                loading="lazy"
                :src="item.thumbnail"
                alt="缩略图"
                class="w-full h-32 object-cover rounded cursor-pointer border border-gray-200"
                @click="openImagePreview(item.thumbnail)"
              />
            </div>

            <!-- 用户信息 -->
            <div v-if="item.user" class="mb-3 pt-3 border-t border-gray-200">
              <p class="text-xs font-semibold text-gray-600 mb-2">用户信息:</p>
              <div class="flex items-center gap-2">
                <img
                  loading="lazy"
                  v-if="item.user.image"
                  :src="item.user.image"
                  :alt="item.user.name"
                  class="w-8 h-8 rounded-full"
                />
                <div class="flex-1">
                  <p class="text-xs font-medium text-gray-800">
                    {{ item.user.name || item.user.firstName || "未知" }}
                  </p>
                  <p v-if="item.user.uuid" class="text-xs text-gray-500">
                    {{ item.user.uuid }}
                  </p>
                </div>
              </div>
            </div>

            <!-- 权限信息 -->
            <div v-if="item.permissions" class="mb-3 p-2 bg-gray-50 rounded">
              <p class="text-xs font-semibold text-gray-600 mb-1">权限:</p>
              <div class="flex flex-wrap gap-1 text-xs">
                <span
                  v-if="item.permissions.canRead"
                  class="px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                >
                  可读
                </span>
                <span
                  v-if="item.permissions.canCopyPrompt"
                  class="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                >
                  可复制提示词
                </span>
                <span
                  v-if="item.permissions.canPublish"
                  class="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                >
                  可发布
                </span>
                <span
                  v-if="item.permissions.canDelete"
                  class="px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                >
                  可删除
                </span>
                <span
                  v-if="item.permissions.canCreateSimilar"
                  class="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700"
                >
                  可创建相似
                </span>
              </div>
            </div>

            <!-- 生成记录 -->
            <div
              v-if="item.generateRecord"
              class="mb-3 pt-3 border-t border-gray-200"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">生成记录:</p>
              <div class="text-xs text-gray-500 space-y-1">
                <p v-if="item.generateRecord.type">
                  <span class="font-medium">类型:</span>
                  {{ item.generateRecord.type }}
                </p>
                <p v-if="item.generateRecord.processType">
                  <span class="font-medium">处理类型:</span>
                  {{ item.generateRecord.processType }}
                </p>
                <p v-if="item.generateRecord.videoModelLabel">
                  <span class="font-medium">视频模型:</span>
                  {{ item.generateRecord.videoModelLabel }}
                </p>
                <p v-if="item.generateRecord.credit">
                  <span class="font-medium">积分:</span>
                  {{ item.generateRecord.credit }}
                  <span
                    v-if="item.generateRecord.creditType"
                    class="text-gray-400"
                  >
                    ({{ item.generateRecord.creditType }})
                  </span>
                </p>
                <p v-if="item.generateRecord.timeCostSeconds">
                  <span class="font-medium">耗时:</span>
                  {{ item.generateRecord.timeCostSeconds }}秒
                </p>
                <p v-if="item.generateRecord.status">
                  <span class="font-medium">状态:</span>
                  <span
                    :class="[
                      'ml-1 px-1.5 py-0.5 rounded text-xs',
                      item.generateRecord.status === 'succeed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700',
                    ]"
                  >
                    {{ item.generateRecord.status }}
                  </span>
                </p>
              </div>

              <!-- 生成模板 -->
              <div
                v-if="item.generateRecord.generationTemplate"
                class="mt-2 p-2 bg-indigo-50 rounded"
              >
                <p class="text-xs font-semibold text-gray-700 mb-1">模板:</p>
                <p class="text-xs text-gray-600">
                  {{ item.generateRecord.generationTemplate.title }}
                </p>
                <p
                  v-if="item.generateRecord.generationTemplate.templateCode"
                  class="text-xs text-gray-500 mt-1"
                >
                  代码:
                  {{ item.generateRecord.generationTemplate.templateCode }}
                </p>
                <p
                  v-if="
                    item.generateRecord.generationTemplate.usedCount !==
                    undefined
                  "
                  class="text-xs text-gray-500"
                >
                  使用次数:
                  {{ item.generateRecord.generationTemplate.usedCount }}
                </p>
              </div>

              <!-- 模板图片 -->
              <div
                v-if="
                  item.generateRecord.templateImage &&
                  item.generateRecord.templateImage.length > 0
                "
                class="mt-2"
              >
                <p class="text-xs font-semibold text-gray-600 mb-1">
                  模板图片:
                </p>
                <div class="flex gap-2 overflow-x-auto">
                  <img
                    loading="lazy"
                    v-for="(img, idx) in item.generateRecord.templateImage"
                    :key="idx"
                    :src="img"
                    :alt="`模板图片 ${idx + 1}`"
                    class="w-16 h-16 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                    @click="openImagePreview(img)"
                  />
                </div>
              </div>

              <!-- 处理配置 -->
              <div
                v-if="item.generateRecord.processConfig"
                class="mt-2 p-2 bg-gray-50 rounded"
              >
                <p class="text-xs font-semibold text-gray-600 mb-1">
                  处理配置:
                </p>
                <div
                  v-if="item.generateRecord.processConfig.prompt"
                  class="mb-1"
                >
                  <p class="text-xs font-medium text-gray-600">Prompt:</p>
                  <p
                    class="text-xs text-gray-500 leading-relaxed wrap-break-word max-h-[100px] overflow-y-auto p-1 bg-white rounded border-l-2 border-indigo-500"
                  >
                    {{ item.generateRecord.processConfig.prompt }}
                  </p>
                </div>
                <div
                  v-if="item.generateRecord.processConfig.image"
                  class="mt-2"
                >
                  <p class="text-xs font-medium text-gray-600 mb-1">图片:</p>
                  <img
                    loading="lazy"
                    :src="item.generateRecord.processConfig.image"
                    alt="处理配置图片"
                    class="w-24 h-24 object-cover rounded cursor-pointer border border-gray-200"
                    @click="
                      openImagePreview(item.generateRecord.processConfig.image)
                    "
                  />
                </div>
              </div>

              <!-- 生成配置 -->
              <div
                v-if="item.generateRecord.generationConfig"
                class="mt-2 p-2 bg-blue-50 rounded"
              >
                <p class="text-xs font-semibold text-gray-600 mb-1">
                  生成配置:
                </p>
                <div class="text-xs text-gray-500 space-y-0.5">
                  <p v-if="item.generateRecord.generationConfig.configType">
                    <span class="font-medium">配置类型:</span>
                    {{ item.generateRecord.generationConfig.configType }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.aspectRatio">
                    <span class="font-medium">宽高比:</span>
                    {{ item.generateRecord.generationConfig.aspectRatio }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.templateCode">
                    <span class="font-medium">模板代码:</span>
                    {{ item.generateRecord.generationConfig.templateCode }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.entryCode">
                    <span class="font-medium">入口代码:</span>
                    {{ item.generateRecord.generationConfig.entryCode }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.numOutputs">
                    <span class="font-medium">输出数量:</span>
                    {{ item.generateRecord.generationConfig.numOutputs }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.outputFormat">
                    <span class="font-medium">输出格式:</span>
                    {{ item.generateRecord.generationConfig.outputFormat }}
                  </p>
                  <p v-if="item.generateRecord.generationConfig.outputQuality">
                    <span class="font-medium">输出质量:</span>
                    {{ item.generateRecord.generationConfig.outputQuality }}
                  </p>
                </div>

                <!-- 变量 -->
                <div
                  v-if="
                    item.generateRecord.generationConfig.variables &&
                    item.generateRecord.generationConfig.variables.length > 0
                  "
                  class="mt-2"
                >
                  <p class="text-xs font-semibold text-gray-600 mb-1">变量:</p>
                  <div class="space-y-1">
                    <div
                      v-for="(variable, idx) in item.generateRecord
                        .generationConfig.variables"
                      :key="idx"
                      class="p-1 bg-white rounded text-xs"
                    >
                      <span class="font-medium">{{ variable.key }}:</span>
                      <span class="text-gray-600">{{ variable.value }}</span>
                    </div>
                  </div>
                </div>

                <!-- 资源对象 -->
                <!-- <div
                  v-if="item.generateRecord.generationConfig.resourceObj"
                  class="mt-2 p-1 bg-white rounded text-xs"
                >
                  <p class="font-semibold text-gray-600 mb-0.5">资源:</p>
                  <p class="text-gray-500">
                    {{
                      item.generateRecord.generationConfig.resourceObj
                        .resource_type
                    }}
                    /
                    {{
                      item.generateRecord.generationConfig.resourceObj
                        .resource_id
                    }}
                  </p>
                </div> -->
              </div>
            </div>

            <!-- 日期信息 -->
            <div
              class="text-xs text-gray-400 space-y-0.5 pt-2 border-t border-gray-200"
            >
              <p v-if="item.createdDate">
                <span class="font-medium">创建:</span>
                {{ new Date(item.createdDate).toLocaleString("zh-CN") }}
              </p>
              <p v-if="item.updatedDate">
                <span class="font-medium">更新:</span>
                {{ new Date(item.updatedDate).toLocaleString("zh-CN") }}
              </p>
              <p v-if="item.publishDate">
                <span class="font-medium">发布:</span>
                {{ new Date(item.publishDate).toLocaleString("zh-CN") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 数据 A 展示 -->
    <div v-if="activeTab === 'a'">
      <div v-for="category in a" :key="category.id" class="mb-12">
        <h2
          class="text-2xl font-semibold text-gray-700 mb-5 pb-2 border-b-2 border-gray-300 uppercase"
        >
          {{ category.id }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div
            v-for="item in category.data"
            :key="item.title"
            class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
          >
            <div
              class="bg-linear-to-r from-indigo-500 to-purple-600 text-white p-4 flex justify-between items-center"
            >
              <h3 class="text-lg font-semibold flex-1">
                {{ item.title_str || item.title }}
              </h3>
              <span class="text-xs opacity-80 bg-white/20 px-2 py-1 rounded">{{
                item.title
              }}</span>
            </div>

            <div
              v-if="item.images && item.images.length > 0"
              class="flex flex-wrap gap-2 p-4 bg-gray-50"
            >
              <img
                loading="lazy"
                @click="openImagePreview(img.url)"
                v-for="(img, idx) in item.images"
                :key="idx"
                :src="img.url"
                :alt="item.title_str"
                class="flex-1 min-w-[150px] max-h-[300px] rounded-lg object-cover"
              />
            </div>

            <div class="p-4">
              <div class="flex flex-wrap gap-2 mb-3">
                <span
                  v-if="item.modelName"
                  class="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                >
                  模型: {{ item.modelName }}
                </span>
                <span
                  v-if="item.videoModel"
                  class="inline-block px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                >
                  视频模型: {{ item.videoModel }}
                </span>
              </div>

              <div class="mt-3">
                <p class="text-sm font-semibold text-gray-600 mb-2">Prompt:</p>
                <p
                  class="text-xs leading-relaxed text-gray-600 wrap-break-word max-h-[150px] overflow-y-auto p-2 bg-gray-50 rounded border-l-4 border-indigo-500"
                >
                  {{ item.prompt }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 数据 B 展示 -->
    <div v-if="activeTab === 'b'">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="item in b"
          :key="item.id"
          class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- 封面图片 -->
          <div class="relative">
            <img
              loading="lazy"
              v-if="item.coverImg"
              :src="item.coverImg"
              :alt="item.title"
              class="w-full h-48 object-cover"
              @click="openImagePreview(item.coverImg)"
            />
            <div
              v-else
              class="w-full h-48 bg-gray-200 flex items-center justify-center"
            >
              <span class="text-gray-400">无封面</span>
            </div>
            <div
              v-if="item.templateCredit"
              class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium"
            >
              {{ item.templateCredit }} 积分
            </div>
          </div>

          <!-- CoverWebps 图片 -->
          <div
            v-if="item.coverWebps && item.coverWebps.length > 0"
            class="p-4 bg-gray-50 border-t border-gray-200"
          >
            <p class="text-xs font-semibold text-gray-600 mb-2">Cover Webps:</p>
            <div class="flex gap-2 overflow-x-auto">
              <img
                loading="lazy"
                v-for="(webp, idx) in item.coverWebps"
                :key="idx"
                :src="webp"
                :alt="`${item.title} cover ${idx + 1}`"
                class="w-24 h-24 object-cover rounded shrink-0 border border-gray-200"
                @click="openImagePreview(webp)"
              />
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="p-4">
            <div class="flex items-start justify-between mb-2">
              <h3 class="text-lg font-semibold text-gray-800 flex-1">
                {{ item.title }}
              </h3>
              <div
                v-if="item.starNum"
                class="flex items-center text-yellow-500 ml-2"
              >
                <span class="text-sm">⭐</span>
                <span class="text-xs ml-1">{{ item.starNum }}</span>
              </div>
            </div>

            <div class="flex flex-wrap gap-2 mb-3">
              <span
                v-if="item.categoryName"
                class="inline-block px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
              >
                {{ item.categoryName }}
              </span>
              <span
                v-if="item.videoType"
                class="inline-block px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700"
              >
                {{ item.videoType }}
              </span>
            </div>

            <div class="text-xs text-gray-500 mb-3">
              <p v-if="item.templateCode" class="mb-1">
                <span class="font-medium">模板代码:</span>
                {{ item.templateCode }}
              </p>
              <p v-if="item.usedCount" class="mb-1">
                <span class="font-medium">使用次数:</span> {{ item.usedCount }}
              </p>
              <p v-if="item.aspectRatio" class="mb-1">
                <span class="font-medium">宽高比:</span> {{ item.aspectRatio }}
              </p>
            </div>

            <!-- 推荐配置图片 -->
            <div
              v-if="
                item.recommendConfigs &&
                item.recommendConfigs.length > 0 &&
                item.recommendConfigs[0].images
              "
              class="mt-3"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">示例图片:</p>
              <div class="flex gap-2 overflow-x-auto">
                <img
                  loading="lazy"
                  v-for="(img, idx) in item.recommendConfigs[0].images"
                  :key="idx"
                  :src="img"
                  :alt="`${item.title} 示例 ${idx + 1}`"
                  class="w-20 h-20 object-cover rounded shrink-0"
                  @click="openImagePreview(img)"
                />
              </div>
            </div>

            <!-- 创建者信息 -->
            <div
              v-if="item.creator"
              class="mt-3 pt-3 border-t border-gray-200 flex items-center"
            >
              <img
                loading="lazy"
                v-if="item.creator.image"
                :src="item.creator.image"
                :alt="item.creator.name"
                class="w-6 h-6 rounded-full mr-2"
              />
              <span class="text-xs text-gray-600">{{ item.creator.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Photo Effects Inspirations 展示 -->
    <div v-if="activeTab === 'photo-inspirations'">
      <!-- 组标题标签选择区域 -->
      <div class="mb-6">
        <div class="mb-4 text-sm text-gray-600">
          <span class="font-semibold">标签统计:</span>
          总计 {{ groupedPhotoInspirations.length }} 个标签
        </div>
        <div class="flex flex-wrap gap-3">
          <button
            v-for="group in groupedPhotoInspirations"
            :key="group.title"
            @click="selectedPhotoInspirationsGroupTitle = group.title"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all duration-200',
              selectedPhotoInspirationsGroupTitle === group.title
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-indigo-400',
            ]"
          >
            {{ group.title }} ({{ group.items.length }})
          </button>
        </div>
      </div>

      <!-- 当前选中组的数据展示 -->
      <div v-if="currentPhotoInspirationsGroup" class="mb-12">
        <h2
          class="text-2xl font-semibold text-gray-700 mb-5 pb-2 border-b-2 border-gray-300"
        >
          {{ currentPhotoInspirationsGroup.title }} ({{
            currentPhotoInspirationsGroup.items.length
          }})
        </h2>

        <!-- 分组提示词汇总（可折叠） -->
        <div
          v-if="
            currentPhotoInspirationsGroup.prompts &&
            currentPhotoInspirationsGroup.prompts.length > 0
          "
          class="mt-4 mb-6"
        >
          <button
            @click="togglePrompt(currentPhotoInspirationsGroup.title)"
            class="flex items-center justify-between w-full px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <span class="text-sm font-semibold text-gray-700">
              提示词汇总 ({{ currentPhotoInspirationsGroup.prompts.length }})
            </span>
            <span class="text-gray-500 text-sm">
              {{
                promptExpanded[currentPhotoInspirationsGroup.title]
                  ? "收起"
                  : "展开"
              }}
              <span class="ml-1">
                {{
                  promptExpanded[currentPhotoInspirationsGroup.title]
                    ? "▲"
                    : "▼"
                }}
              </span>
            </span>
          </button>
          <div
            v-show="promptExpanded[currentPhotoInspirationsGroup.title]"
            class="mt-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div class="space-y-3">
              <div
                v-for="(prompt, idx) in currentPhotoInspirationsGroup.prompts"
                :key="idx"
                class="p-3 bg-gray-50 rounded border-l-4 border-indigo-500"
              >
                <p class="text-xs text-gray-600 leading-relaxed">
                  {{ prompt }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          <div
            v-for="item in currentPhotoInspirationsGroup.items"
            :key="item.videoId"
            class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
          >
            <!-- 封面图片 -->
            <div class="relative">
              <img
                loading="lazy"
                v-if="item.cover"
                :src="item.cover"
                :alt="item.title || '视频封面'"
                class="w-full h-48 object-cover"
                @click="openImagePreview(item.cover)"
              />
              <div
                v-else
                class="w-full h-48 bg-gray-200 flex items-center justify-center"
              >
                <span class="text-gray-400">无封面</span>
              </div>
              <div
                v-if="item.starNum"
                class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
              >
                <span>⭐</span>
                <span>{{ item.starNum }}</span>
              </div>
              <div
                v-if="item.mediaType"
                class="absolute top-2 left-2 bg-blue-600/80 text-white px-2 py-1 rounded text-xs font-medium"
              >
                {{ item.mediaType }}
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="p-4">
              <div class="flex items-start justify-between mb-2">
                <h3 class="text-lg font-semibold text-gray-800 flex-1">
                  {{ item.generateRecord.generationTemplate.title || "无标题" }}
                </h3>
              </div>

              <!-- 详细信息 -->
              <div class="text-xs text-gray-500 mb-3 space-y-1">
                <p v-if="item.videoId">
                  <span class="font-medium">ID:</span> {{ item.videoId }}
                </p>
                <p v-if="item.videoRatio">
                  <span class="font-medium">比例:</span> {{ item.videoRatio }}
                </p>
                <p v-if="item.score">
                  <span class="font-medium">评分:</span> {{ item.score }}
                </p>
              </div>

              <!-- 链接 -->
              <div
                v-if="item.videoUrl || item.videoUrlNoWatermark"
                class="mb-3"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">链接:</p>
                <div class="flex gap-3">
                  <a
                    v-if="item.videoUrl"
                    :href="item.videoUrl"
                    target="_blank"
                    class="text-xs text-blue-600 hover:underline truncate"
                  >
                    带水印
                  </a>
                  <a
                    v-if="item.videoUrlNoWatermark"
                    :href="item.videoUrlNoWatermark"
                    target="_blank"
                    class="text-xs text-blue-600 hover:underline truncate"
                  >
                    无水印
                  </a>
                </div>
              </div>

              <!-- 生成记录 -->
              <div
                v-if="item.generateRecord"
                class="mt-3 pt-3 border-t border-gray-200"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">
                  生成记录:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.generateRecord.type">
                    <span class="font-medium">类型:</span>
                    {{ item.generateRecord.type }}
                  </p>
                  <p v-if="item.generateRecord.processTypeLabel">
                    <span class="font-medium">处理类型:</span>
                    {{ item.generateRecord.processTypeLabel }}
                  </p>
                  <div
                    v-if="
                      item.generateRecord.templateImage &&
                      item.generateRecord.templateImage.length > 0
                    "
                    class="mt-2"
                  >
                    <p class="text-xs font-semibold text-gray-600 mb-1">
                      模板图片:
                    </p>
                    <div class="flex gap-2 overflow-x-auto">
                      <img
                        loading="lazy"
                        v-for="(img, idx) in item.generateRecord.templateImage"
                        :key="idx"
                        :src="img"
                        :alt="`模板图片 ${idx + 1}`"
                        class="w-16 h-16 object-cover rounded shrink-0 border border-gray-200"
                        @click="openImagePreview(img)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Photo Effects 展示 -->
    <div v-if="activeTab === 'photo-effects'">
      <!-- 组类别标签选择区域 -->
      <div class="mb-6">
        <div class="flex flex-wrap gap-3">
          <button
            v-for="group in groupedPhotoEffects"
            :key="group.category"
            @click="selectedPhotoEffectsCategory = group.category"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all duration-200',
              selectedPhotoEffectsCategory === group.category
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-indigo-400',
            ]"
          >
            {{ group.category }} ({{ group.items.length }})
          </button>
        </div>
      </div>

      <!-- 当前选中组的数据展示 -->
      <div v-if="currentPhotoEffectsGroup" class="mb-12">
        <h2
          class="text-2xl font-semibold text-gray-700 mb-5 pb-2 border-b-2 border-gray-300"
        >
          {{ currentPhotoEffectsGroup.category }} ({{
            currentPhotoEffectsGroup.items.length
          }})
        </h2>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          <div
            v-for="item in currentPhotoEffectsGroup.items"
            :key="item.id"
            @click="
              navigateToPhotoInspirationsFromPhotoEffects(
                item.title || item.originalTitle
              )
            "
            :class="[
              'bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer',
              hasMatchingGroupInPhotoInspirations(
                item.title || item.originalTitle
              )
                ? 'ring-4 ring-indigo-500 ring-opacity-60 border-2 border-indigo-400'
                : '',
            ]"
          >
            <!-- 封面图片 -->
            <div class="relative">
              <img
                loading="lazy"
                v-if="item.coverImg"
                :src="item.coverImg"
                :alt="item.title"
                class="w-full h-48 object-cover cursor-pointer"
                @click.stop="openImagePreview(item.coverImg)"
              />
              <div
                v-else
                class="w-full h-48 bg-gray-200 flex items-center justify-center"
              >
                <span class="text-gray-400">无封面</span>
              </div>
              <div
                v-if="item.templateCredit"
                class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium"
              >
                {{ item.templateCredit }} 积分
              </div>
              <div
                v-if="item.starNum"
                class="absolute top-2 left-2 bg-yellow-500/80 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
              >
                <span>⭐</span>
                <span>{{ item.starNum }}</span>
              </div>
            </div>

            <!-- CoverWebps 图片 -->
            <div
              v-if="item.coverWebps && item.coverWebps.length > 0"
              class="p-4 bg-gray-50 border-t border-gray-200"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webps:
              </p>
              <div class="flex gap-2 overflow-x-auto">
                <img
                  loading="lazy"
                  v-for="(webp, idx) in item.coverWebps"
                  :key="idx"
                  :src="webp"
                  :alt="`${item.title} cover ${idx + 1}`"
                  class="w-24 h-24 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                  @click.stop="openImagePreview(webp)"
                />
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="p-4">
              <div class="flex items-start justify-between mb-2">
                <h3 class="text-lg font-semibold text-gray-800 flex-1">
                  {{ item.title || item.originalTitle || "无标题" }}
                </h3>
              </div>

              <div class="text-xs text-gray-500 mb-3 space-y-1">
                <p v-if="item.id">
                  <span class="font-medium">ID:</span> {{ item.id }}
                </p>
                <p v-if="item.templateCode">
                  <span class="font-medium">模板代码:</span>
                  {{ item.templateCode }}
                </p>
                <p v-if="item.usedCount !== undefined">
                  <span class="font-medium">使用次数:</span>
                  {{ item.usedCountLabel || item.usedCount }}
                </p>
                <p v-if="item.aspectRatio">
                  <span class="font-medium">宽高比:</span>
                  {{ item.aspectRatio }}
                </p>
                <p
                  v-if="
                    item.supportedAspectRatio &&
                    item.supportedAspectRatio.length > 0
                  "
                >
                  <span class="font-medium">支持的宽高比:</span>
                  {{ item.supportedAspectRatio.join(", ") }}
                </p>
                <p v-if="item.assetsNum">
                  <span class="font-medium">资源数量:</span>
                  {{ item.assetsNum }}
                </p>
                <p v-if="item.assetsType">
                  <span class="font-medium">资源类型:</span>
                  {{ item.assetsType }}
                </p>
                <p v-if="item.maxLength">
                  <span class="font-medium">最大长度:</span>
                  {{ item.maxLength }}
                </p>
                <p v-if="item.createdDate">
                  <span class="font-medium">创建日期:</span>
                  {{ new Date(item.createdDate).toLocaleString("zh-CN") }}
                </p>
                <p v-if="item.source">
                  <span class="font-medium">来源:</span> {{ item.source }}
                </p>
              </div>

              <!-- 模板标记 -->
              <!-- <div
                v-if="item.templateMarks && item.templateMarks.length > 0"
                class="mb-3"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">标记:</p>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(mark, idx) in item.templateMarks"
                    :key="idx"
                    class="inline-block px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700"
                  >
                    {{ mark }}
                  </span>
                </div>
              </div> -->

              <!-- 推荐配置图片 -->
              <!-- <div
                v-if="item.recommendConfigs && item.recommendConfigs.length > 0"
                class="mb-3"
              >
                <div
                  v-for="(config, configIdx) in item.recommendConfigs"
                  :key="configIdx"
                  class="mb-3"
                >
                  <p class="text-xs font-semibold text-gray-600 mb-2">
                    {{ config.title || config.code || "示例图片" }}:
                  </p>
                  <div
                    v-if="config.images && config.images.length > 0"
                    class="flex gap-2 overflow-x-auto"
                  >
                                  <img
              loading="lazy"
                      v-for="(img, idx) in config.images"
                      :key="idx"
                      :src="img"
                      :alt="`${item.title} ${config.title || ''} ${idx + 1}`"
                      class="w-20 h-20 object-cover rounded shrink-0 cursor-pointer border border-gray-200"
                      @click.stop="openImagePreview(img)"
                    />
                  </div>
                </div>
              </div> -->

              <!-- 变量配置 -->
              <!-- <div
                v-if="item.variableConfigs && item.variableConfigs.length > 0"
                class="mb-3"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">
                  变量配置:
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(config, idx) in item.variableConfigs"
                    :key="idx"
                    class="p-2 bg-gray-50 rounded text-xs"
                  >
                    <p v-if="config.key">
                      <span class="font-medium">键:</span> {{ config.key }}
                    </p>
                    <p v-if="config.label">
                      <span class="font-medium">标签:</span> {{ config.label }}
                    </p>
                    <p v-if="config.defaultValue">
                      <span class="font-medium">默认值:</span>
                      {{ config.defaultValue }}
                    </p>
                    <p v-if="config.ideaType">
                      <span class="font-medium">想法类型:</span>
                      {{ config.ideaType }}
                    </p>
                    <p v-if="config.optional !== undefined">
                      <span class="font-medium">可选:</span>
                      {{ config.optional ? "是" : "否" }}
                    </p>
                  </div>
                </div>
              </div> -->

              <!-- Prompt -->
              <!-- <div v-if="item.prompt" class="mb-3">
                <p class="text-xs font-semibold text-gray-600 mb-2">Prompt:</p>
                <p
                  class="text-xs leading-relaxed text-gray-600 wrap-break-word max-h-[100px] overflow-y-auto p-2 bg-gray-50 rounded border-l-4 border-indigo-500"
                >
                  {{ item.prompt }}
                </p>
              </div> -->

              <!-- 创建者信息 -->
              <div
                v-if="item.creator || item.user"
                class="mt-3 pt-3 border-t border-gray-200 flex items-center"
              >
                <img
                  loading="lazy"
                  v-if="(item.creator || item.user)?.image"
                  :src="(item.creator || item.user).image"
                  :alt="(item.creator || item.user).name"
                  class="w-6 h-6 rounded-full mr-2"
                />
                <span class="text-xs text-gray-600">{{
                  (item.creator || item.user)?.name || "未知"
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Effects Inspirations 展示 -->
    <div v-if="activeTab === 'video-inspirations'">
      <!-- 组标题标签选择区域 -->
      <div class="mb-6">
        <div class="mb-4 text-sm text-gray-600">
          <span class="font-semibold">标签统计:</span>
          总计 {{ groupedVideoInspirations.totalGroups }} 个标签
          {{
            groupedVideoInspirations.groupsWithConfig > 0
              ? ` / ${groupedVideoInspirations.groupsWithConfig} 个有配置`
              : ""
          }}
        </div>
        <div class="flex flex-wrap gap-3">
          <button
            v-for="group in groupedVideoInspirations.groups"
            :key="group.title"
            @click="selectedVideoInspirationsGroupTitle = group.title"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all duration-200',
              selectedVideoInspirationsGroupTitle === group.title
                ? 'bg-indigo-600 text-white shadow-md'
                : group.hasWorkflowConfig
                ? 'bg-white text-gray-700 border-2 border-indigo-400 hover:bg-gray-50'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-indigo-400',
            ]"
          >
            {{ group.title }} ({{ group.items.length }})
          </button>
        </div>
      </div>

      <!-- 当前选中组的数据展示 -->
      <div v-if="currentVideoInspirationsGroup" class="mb-12">
        <h2
          class="text-2xl font-semibold text-gray-700 mb-5 pb-2 border-b-2 border-gray-300"
        >
          {{ currentVideoInspirationsGroup.title }} ({{
            currentVideoInspirationsGroup.items.length
          }})
        </h2>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          <div
            v-for="item in currentVideoInspirationsGroup.items"
            :key="item.videoId"
            class="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
          >
            <!-- 封面图片 -->
            <div class="relative">
              <img
                loading="lazy"
                v-if="item.cover"
                :src="item.cover"
                :alt="item.title || '视频封面'"
                class="w-full h-48 object-cover"
                @click="openImagePreview(item.cover)"
              />
              <div
                v-else
                class="w-full h-48 bg-gray-200 flex items-center justify-center"
              >
                <span class="text-gray-400">无封面</span>
              </div>
              <div
                v-if="item.starNum"
                class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
              >
                <span>⭐</span>
                <span>{{ item.starNum }}</span>
              </div>
              <div
                v-if="item.mediaType"
                class="absolute top-2 left-2 bg-blue-600/80 text-white px-2 py-1 rounded text-xs font-medium"
              >
                {{ item.mediaType }}
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="p-4">
              <div class="flex items-start justify-between mb-2">
                <h3 class="text-lg font-semibold text-gray-800 flex-1">
                  {{ item.generateRecord.generationTemplate.title || "无标题" }}
                </h3>
              </div>

              <!-- 详细信息 -->
              <div class="text-xs text-gray-500 mb-3 space-y-1">
                <p v-if="item.videoId">
                  <span class="font-medium">ID:</span> {{ item.videoId }}
                </p>
                <p v-if="item.videoRatio">
                  <span class="font-medium">比例:</span> {{ item.videoRatio }}
                </p>
                <p v-if="item.score">
                  <span class="font-medium">评分:</span> {{ item.score }}
                </p>
              </div>

              <!-- 链接 -->
              <div
                v-if="item.videoUrl || item.videoUrlNoWatermark"
                class="mb-3"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">链接:</p>
                <div class="flex gap-3">
                  <a
                    v-if="item.videoUrl"
                    :href="item.videoUrl"
                    target="_blank"
                    class="text-xs text-blue-600 hover:underline truncate"
                  >
                    带水印
                  </a>
                  <a
                    v-if="item.videoUrlNoWatermark"
                    :href="item.videoUrlNoWatermark"
                    target="_blank"
                    class="text-xs text-blue-600 hover:underline truncate"
                  >
                    无水印
                  </a>
                </div>
              </div>

              <!-- 生成记录 -->
              <div
                v-if="item.generateRecord"
                class="mt-3 pt-3 border-t border-gray-200"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">
                  生成记录:
                </p>
                <div class="text-xs text-gray-500 space-y-1">
                  <p v-if="item.generateRecord.type">
                    <span class="font-medium">类型:</span>
                    {{ item.generateRecord.type }}
                  </p>
                  <p v-if="item.generateRecord.processTypeLabel">
                    <span class="font-medium">处理类型:</span>
                    {{ item.generateRecord.processTypeLabel }}
                  </p>
                  <div
                    v-if="
                      item.generateRecord.templateImage &&
                      item.generateRecord.templateImage.length > 0
                    "
                    class="mt-2"
                  >
                    <p class="text-xs font-semibold text-gray-600 mb-1">
                      模板图片:
                    </p>
                    <div class="flex gap-2 overflow-x-auto">
                      <img
                        loading="lazy"
                        v-for="(img, idx) in item.generateRecord.templateImage"
                        :key="idx"
                        :src="img"
                        :alt="`模板图片 ${idx + 1}`"
                        class="w-16 h-16 object-cover rounded shrink-0 border border-gray-200"
                        @click="openImagePreview(img)"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Workflow Config -->
              <div
                v-if="item.generateRecord?.generationConfig?.workflowConfig"
                class="mt-3 pt-3 border-t border-gray-200"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">
                  Workflow Config:
                </p>
                <!-- Workflow Config dag -->
                <div class="mb-3">
                  <p class="text-xs font-semibold text-gray-600 mb-2">dag:</p>
                  <div
                    class="w-full bg-gray-50 rounded border border-gray-200 flex items-center justify-center overflow-x-auto"
                    style="max-width: 100%"
                  >
                    <img
                      loading="lazy"
                      :src="`/pollo_ai/video_workflow_dag/${item.generateRecord.generationTemplate.templateCode}.png`"
                      alt="Workflow Config dag"
                      class="max-w-full max-h-48 object-contain rounded cursor-pointer"
                      style="display: block"
                      @click="
                        openImagePreview(
                          `/pollo_ai/video_workflow_dag/${item.generateRecord.generationTemplate.templateCode}.png`
                        )
                      "
                    />
                  </div>
                </div>

                <div class="text-xs text-gray-500 space-y-3">
                  <!-- genImage -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genImage
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-blue-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">Gen Image:</p>
                    <div class="space-y-1">
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.enable !== undefined
                        "
                      >
                        <span class="font-medium">启用:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.enable
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.model
                        "
                      >
                        <span class="font-medium">模型:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.model
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.aspectRatio
                        "
                      >
                        <span class="font-medium">宽高比:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.aspectRatio
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.prompt
                        "
                        class="mt-2"
                      >
                        <span class="font-medium">Prompt:</span>
                        <span
                          class="block mt-1 p-2 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[200px] overflow-y-auto"
                        >
                          {{
                            item.generateRecord.generationConfig.workflowConfig
                              .genImage.prompt
                          }}
                        </span>
                      </p>
                      <div
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genImage.image
                        "
                        class="mt-2"
                      >
                        <p class="font-medium mb-1">图片:</p>
                        <img
                          loading="lazy"
                          :src="
                            item.generateRecord.generationConfig.workflowConfig
                              .genImage.image
                          "
                          alt="Gen Image"
                          class="w-32 h-32 object-cover rounded border border-gray-200 cursor-pointer"
                          @click="
                            openImagePreview(
                              item.generateRecord.generationConfig
                                .workflowConfig.genImage.image
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>

                  <!-- genVideo -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genVideo && item.generateRecord.generationConfig.workflowConfig.genVideo.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-green-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">Gen Video:</p>
                    <div class="space-y-1">
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.enable !== undefined
                        "
                      >
                        <span class="font-medium">启用:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.enable
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.model
                        "
                      >
                        <span class="font-medium">模型:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.model
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.resolution
                        "
                      >
                        <span class="font-medium">分辨率:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.resolution
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.aspectRatio
                        "
                      >
                        <span class="font-medium">宽高比:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.aspectRatio
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.length
                        "
                      >
                        <span class="font-medium">长度:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.length
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.prompt
                        "
                        class="mt-2"
                      >
                        <span class="font-medium">Prompt:</span>
                        <span
                          class="block mt-1 p-2 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[200px] overflow-y-auto"
                        >
                          {{
                            item.generateRecord.generationConfig.workflowConfig
                              .genVideo.prompt
                          }}
                        </span>
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.promptOptimizer !== undefined
                        "
                      >
                        <span class="font-medium">Prompt Optimizer:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.promptOptimizer
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.generateAudio !== undefined
                        "
                      >
                        <span class="font-medium">生成音频:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.generateAudio
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.wanAudio !== undefined
                        "
                      >
                        <span class="font-medium">Wan Audio:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genVideo.wanAudio
                            ? "是"
                            : "否"
                        }}
                      </p>
                    </div>
                  </div>

                  <!-- genAudio -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genAudio && item.generateRecord.generationConfig.workflowConfig.genAudio.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-purple-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">Gen Audio:</p>
                    <div class="space-y-1">
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.enable !== undefined
                        "
                      >
                        <span class="font-medium">启用:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.enable
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.model
                        "
                      >
                        <span class="font-medium">模型:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.model
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.prompt
                        "
                        class="mt-2"
                      >
                        <span class="font-medium">Prompt:</span>
                        <span
                          class="block mt-1 p-2 bg-white rounded text-gray-600 leading-relaxed wrap-break-word max-h-[200px] overflow-y-auto"
                        >
                          {{
                            item.generateRecord.generationConfig.workflowConfig
                              .genAudio.prompt
                          }}
                        </span>
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.length
                        "
                      >
                        <span class="font-medium">长度:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genAudio.length
                        }}
                      </p>
                    </div>
                  </div>

                  <!-- genBgm -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig.genBgm && item.generateRecord.generationConfig.workflowConfig.genBgm.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-yellow-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">Gen BGM:</p>
                    <div class="space-y-1">
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genBgm.enable !== undefined
                        "
                      >
                        <span class="font-medium">启用:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genBgm.enable
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genBgm.bgmUrl
                        "
                      >
                        <span class="font-medium">BGM URL:</span>
                        <a
                          :href="
                            item.generateRecord.generationConfig.workflowConfig
                              .genBgm.bgmUrl
                          "
                          target="_blank"
                          class="text-blue-600 hover:underline break-all"
                        >
                          {{
                            item.generateRecord.generationConfig.workflowConfig
                              .genBgm.bgmUrl
                          }}
                        </a>
                      </p>
                    </div>
                  </div>

                  <!-- genSplice -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genSplice && item.generateRecord.generationConfig.workflowConfig.genSplice.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-red-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">Gen Splice:</p>
                    <div class="space-y-1">
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genSplice.enable !== undefined
                        "
                      >
                        <span class="font-medium">启用:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genSplice.enable
                            ? "是"
                            : "否"
                        }}
                      </p>
                      <p
                        v-if="
                          item.generateRecord.generationConfig.workflowConfig
                            .genSplice.location
                        "
                      >
                        <span class="font-medium">位置:</span>
                        {{
                          item.generateRecord.generationConfig.workflowConfig
                            .genSplice.location
                        }}
                      </p>
                    </div>
                  </div>

                  <!-- genTailImage -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genTailImage && item.generateRecord.generationConfig.workflowConfig.genTailImage.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-indigo-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">
                      Gen Tail Image:
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.enable !== undefined
                      "
                    >
                      <span class="font-medium">启用:</span>
                      {{
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.enable
                          ? "是"
                          : "否"
                      }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.model
                      "
                    >
                      <span class="font-medium">模型:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.model }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.aspectRatio
                      "
                    >
                      <span class="font-medium">宽高比:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.aspectRatio }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.prompt
                      "
                    >
                      <span class="font-medium">Prompt:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.prompt }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.length
                      "
                    >
                      <span class="font-medium">长度:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.length }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.promptOptimizer
                      "
                    >
                      <span class="font-medium">Prompt Optimizer:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.promptOptimizer ? "是" : "否" }}
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genTailImage.generateAudio
                      "
                    >
                      <span class="font-medium">生成音频:</span>
                      {{ item.generateRecord.generationConfig.workflowConfig.genTailImage.generateAudio ? "是" : "否" }}
                    </p>
                  </div>

                  <!-- genVideoEdit -->
                  <div
                    v-if="
                      item.generateRecord.generationConfig.workflowConfig
                        .genVideoEdit && item.generateRecord.generationConfig.workflowConfig.genVideoEdit.enable
                    "
                    class="p-3 bg-gray-50 rounded border-l-4 border-pink-500"
                  >
                    <p class="font-semibold text-gray-700 mb-2">
                      Gen Video Edit:
                    </p>
                    <p
                      v-if="
                        item.generateRecord.generationConfig.workflowConfig
                          .genVideoEdit.enable !== undefined
                      "
                    >
                      <span class="font-medium">启用:</span>
                      {{
                        item.generateRecord.generationConfig.workflowConfig
                          .genVideoEdit.enable
                          ? "是"
                          : "否"
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Effects 展示 -->
    <div v-if="activeTab === 'video-effects'">
      <!-- 组类别标签选择区域 -->
      <div class="mb-6">
        <div class="flex flex-wrap gap-3">
          <button
            v-for="group in groupedVideoEffects"
            :key="group.category"
            @click="selectedVideoEffectsCategory = group.category"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all duration-200',
              selectedVideoEffectsCategory === group.category
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-indigo-400',
            ]"
          >
            {{ group.category }} ({{ group.items.length }})
          </button>
        </div>
      </div>

      <!-- 当前选中组的数据展示 -->
      <div v-if="currentVideoEffectsGroup" class="mb-12">
        <h2
          class="text-2xl font-semibold text-gray-700 mb-5 pb-2 border-b-2 border-gray-300"
        >
          {{ currentVideoEffectsGroup.category }} ({{
            currentVideoEffectsGroup.items.length
          }})
        </h2>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          <div
            v-for="item in currentVideoEffectsGroup.items"
            :key="item.id"
            @click="
              navigateToVideoInspirationsFromVideoEffects(
                item.title || item.originalTitle
              )
            "
            :class="[
              'bg-white rounded-xl shadow-md overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer',
              hasMatchingGroupInVideoInspirations(
                item.title || item.originalTitle
              )
                ? 'ring-4 ring-indigo-500 ring-opacity-60 border-2 border-indigo-400'
                : '',
            ]"
          >
            <!-- 封面图片 -->
            <div class="relative">
              <img
                loading="lazy"
                v-if="item.coverImg"
                :src="item.coverImg"
                :alt="item.title"
                class="w-full h-48 object-cover cursor-pointer"
                @click.stop="openImagePreview(item.coverImg)"
              />
              <div
                v-else
                class="w-full h-48 bg-gray-200 flex items-center justify-center"
              >
                <span class="text-gray-400">无封面</span>
              </div>
              <div
                v-if="item.templateCredit"
                class="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-medium"
              >
                {{ item.templateCredit }} 积分
              </div>
              <div
                v-if="item.starNum"
                class="absolute top-2 left-2 bg-yellow-500/80 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1"
              >
                <span>⭐</span>
                <span>{{ item.starNum }}</span>
              </div>
            </div>

            <!-- CoverWebps 图片 -->
            <div
              v-if="item.coverWebps && item.coverWebps.length > 0"
              class="p-4 bg-gray-50 border-t border-gray-200"
            >
              <p class="text-xs font-semibold text-gray-600 mb-2">
                Cover Webps:
              </p>
              <div class="flex gap-2 overflow-x-auto">
                <img
                  loading="lazy"
                  v-for="(webp, idx) in item.coverWebps"
                  :key="idx"
                  :src="webp"
                  :alt="`${item.title} cover ${idx + 1}`"
                  class="w-24 h-24 object-cover rounded shrink-0 border border-gray-200 cursor-pointer"
                  @click.stop="openImagePreview(webp)"
                />
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="p-4">
              <div class="flex items-start justify-between mb-2">
                <h3 class="text-lg font-semibold text-gray-800 flex-1">
                  {{ item.title || item.originalTitle || "无标题" }}
                </h3>
              </div>

              <!-- 链接 -->
              <div v-if="item.coverVideo" class="mb-3">
                <p class="text-xs font-semibold text-gray-600 mb-2">链接:</p>
                <div class="flex gap-3">
                  <a
                    v-if="item.coverVideo"
                    :href="item.coverVideo"
                    target="_blank"
                    class="text-xs text-blue-600 hover:underline truncate"
                  >
                    封面视频
                  </a>
                </div>
              </div>

              <div class="text-xs text-gray-500 mb-3 space-y-1">
                <p v-if="item.id">
                  <span class="font-medium">ID:</span> {{ item.id }}
                </p>
                <p v-if="item.templateCode">
                  <span class="font-medium">模板代码:</span>
                  {{ item.templateCode }}
                </p>
                <p v-if="item.usedCount !== undefined">
                  <span class="font-medium">使用次数:</span>
                  {{ item.usedCountLabel || item.usedCount }}
                </p>
                <p v-if="item.aspectRatio">
                  <span class="font-medium">宽高比:</span>
                  {{ item.aspectRatio }}
                </p>
                <p
                  v-if="
                    item.supportedAspectRatio &&
                    item.supportedAspectRatio.length > 0
                  "
                >
                  <span class="font-medium">支持的宽高比:</span>
                  {{ item.supportedAspectRatio.join(", ") }}
                </p>
                <p v-if="item.assetsNum">
                  <span class="font-medium">资源数量:</span>
                  {{ item.assetsNum }}
                </p>
                <p v-if="item.assetsType">
                  <span class="font-medium">资源类型:</span>
                  {{ item.assetsType }}
                </p>
                <p v-if="item.maxLength">
                  <span class="font-medium">最大长度:</span>
                  {{ item.maxLength }}
                </p>
                <p v-if="item.createdDate">
                  <span class="font-medium">创建日期:</span>
                  {{ new Date(item.createdDate).toLocaleString("zh-CN") }}
                </p>
                <p v-if="item.source">
                  <span class="font-medium">来源:</span> {{ item.source }}
                </p>
              </div>

              <!-- 推荐配置图片 -->
              <div
                v-if="item.recommendConfigs && item.recommendConfigs.length > 0"
                class="mb-3"
              >
                <div
                  v-for="(config, configIdx) in item.recommendConfigs"
                  :key="configIdx"
                  class="mb-3"
                >
                  <p class="text-xs font-semibold text-gray-600 mb-2">
                    {{ config.title || config.code || "示例图片" }}:
                  </p>
                  <div
                    v-if="config.images && config.images.length > 0"
                    class="flex gap-2 overflow-x-auto"
                  >
                    <img
                      loading="lazy"
                      v-for="(img, idx) in config.images"
                      :key="idx"
                      :src="img"
                      :alt="`${item.title} ${config.title || ''} ${idx + 1}`"
                      class="w-20 h-20 object-cover rounded shrink-0 cursor-pointer border border-gray-200"
                      @click.stop="openImagePreview(img)"
                    />
                  </div>
                </div>
              </div>

              <!-- 变量配置 -->
              <div
                v-if="item.variableConfigs && item.variableConfigs.length > 0"
                class="mb-3"
              >
                <p class="text-xs font-semibold text-gray-600 mb-2">
                  变量配置:
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(config, idx) in item.variableConfigs"
                    :key="idx"
                    class="p-2 bg-gray-50 rounded text-xs"
                  >
                    <p v-if="config.key">
                      <span class="font-medium">键:</span> {{ config.key }}
                    </p>
                    <p v-if="config.label">
                      <span class="font-medium">标签:</span> {{ config.label }}
                    </p>
                    <p v-if="config.defaultValue">
                      <span class="font-medium">默认值:</span>
                      {{ config.defaultValue }}
                    </p>
                    <p v-if="config.ideaType">
                      <span class="font-medium">想法类型:</span>
                      {{ config.ideaType }}
                    </p>
                    <p v-if="config.optional !== undefined">
                      <span class="font-medium">可选:</span>
                      {{ config.optional ? "是" : "否" }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- 创建者信息 -->
              <div
                v-if="item.creator || item.user"
                class="mt-3 pt-3 border-t border-gray-200 flex items-center"
              >
                <img
                  loading="lazy"
                  v-if="(item.creator || item.user)?.image"
                  :src="(item.creator || item.user).image"
                  :alt="(item.creator || item.user).name"
                  class="w-6 h-6 rounded-full mr-2"
                />
                <span class="text-xs text-gray-600">{{
                  (item.creator || item.user)?.name || "未知"
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>